import React, { useRef, useEffect } from 'react';
import { useElasticSheet } from '../../lib/useElasticSheet';
import type { Vec2 } from '../../lib/elasticSheet';

interface SpotlightCardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
  enablePhysics?: boolean;
  variant?: 'surface' | 'flat';
  shadowOverlay?: string; // Additional shadow string to be merged with dynamic shadow
  sensitivity?: number; // Multiplier for the physics effect magnitude (defaut: 1.0)
}

/**
 * SpotlightCard samples from the shared elastic sheet field.
 * 
 * When enablePhysics = true:
 * - Full physics: tilt, scale, translateZ, dynamic shadow
 * 
 * When enablePhysics = false:
 * - Global only: only translateZ (depth) from global cloth deformation
 * - No local tilt, scale, or dynamic shadow
 */
export default function SpotlightCard({
  children,
  className = '',
  enablePhysics = true,
  variant = 'flat',
  shadowOverlay,
  sensitivity = 1.0,
  ...props
}: SpotlightCardProps) {
  const divRef = useRef<HTMLDivElement>(null);
  const { frameCount, sampleDisplacement, sampleMultiplePoints } = useElasticSheet();

  useEffect(() => {
    if (!divRef.current) return;

    const rect = divRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const samplePoints: Vec2[] = [
      { x: rect.left, y: rect.top },
      { x: rect.right, y: rect.top },
      { x: rect.left, y: rect.bottom },
      { x: rect.right, y: rect.bottom },
      { x: centerX, y: rect.top },
      { x: centerX, y: rect.bottom },
      { x: rect.left, y: centerY },
      { x: rect.right, y: centerY }
    ];

    const samples = sampleMultiplePoints(samplePoints);
    const defaultSample = { displacement: 0, gradientX: 0, gradientY: 0, normal: { x: 0, y: 0, z: 1 } };
    const safeSamples = samplePoints.map((_, index) => samples[index] ?? defaultSample);

    const avgDisplacement = safeSamples.reduce((sum, s) => sum + s.displacement, 0) / safeSamples.length;

    let transformString = '';
    let shadowString = 'none';

    if (enablePhysics) {
      // Full physics: tilt, scale, translateZ, dynamic shadow
      const topAvg = (safeSamples[0].displacement + safeSamples[4].displacement + safeSamples[1].displacement) / 3;
      const bottomAvg = (safeSamples[2].displacement + safeSamples[5].displacement + safeSamples[3].displacement) / 3;
      const leftAvg = (safeSamples[0].displacement + safeSamples[6].displacement + safeSamples[2].displacement) / 3;
      const rightAvg = (safeSamples[1].displacement + safeSamples[7].displacement + safeSamples[3].displacement) / 3;

      const tiltScale = 0.3 * sensitivity;
      const cardArea = rect.width * rect.height;
      // Increased denominator significantly (52000 -> 200000 -> 500000) to make large cards move more (less attenuation)
      // We rely on 'sensitivity' prop to dampen small/compact items instead of pure area-based physics
      const attenuation = 1 / (1 + cardArea / 500000);
      
      const rotateX = (topAvg - bottomAvg) * tiltScale * attenuation;
      const rotateY = (rightAvg - leftAvg) * tiltScale * attenuation;
      
      // Depth calculation
      // Multiply by sensitivity to control z-axis movement too
      const depth = Math.max(0, -avgDisplacement) * attenuation * sensitivity;
      const translateZ = -depth * 0.45;
      
      // Less shrinking effect if sensitivity is low
      const scale = Math.max(0.82, 1 - depth * 0.0023);

      transformString = `perspective(1000px) translateZ(${translateZ}px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(${scale})`;

      const blur = 10 + depth * 2;
      const spread = depth * 0.3;
      const shadowOpacity = Math.min(0.3, 0.08 + depth * 0.008);
      const offsetX = rotateY * 0.4;
      const offsetY = -rotateX * 0.4;

      shadowString = `${offsetX}px ${offsetY}px ${blur}px ${spread}px rgba(0, 0, 0, ${shadowOpacity})`;
    } else {
      // Global only: only translateZ from average displacement
      const cardArea = rect.width * rect.height;
      const attenuation = 1 / (1 + cardArea / 500000);
      const depth = Math.max(0, -avgDisplacement) * attenuation * sensitivity;
      const translateZ = -depth * 0.25;

      transformString = `perspective(1000px) translateZ(${translateZ}px) rotateX(0deg) rotateY(0deg) scale(1)`;
      shadowString = 'none';
    }

    // Apply styles directly to DOM for performance and to bypass React render cycle latency
    divRef.current.style.transform = transformString;
    // Only apply the dynamic physics shadow (drop shadow) here
    // Inner shadows are handled by a separate overlay element to avoid conflicts
    divRef.current.style.boxShadow = shadowString;

  }, [frameCount, enablePhysics, sampleDisplacement, sampleMultiplePoints, sensitivity]);

  const baseStyles = "relative overflow-hidden bg-white/5 rounded-xl transition-colors duration-300";

  // Determine the static shadow style (inner shadow)
  const defaultSurfaceShadow = 'inset 0 1px 0 0 rgba(255,255,255,0.05)';
  const staticShadow = shadowOverlay
    ? shadowOverlay
    : (variant === 'surface' ? defaultSurfaceShadow : undefined);
  
  return (
    <div
      ref={divRef}
      className={`${baseStyles} ${className}`}
      style={{
        willChange: 'transform, box-shadow',
        transformStyle: 'preserve-3d',
        ...props.style
      }}
      {...props}
    >
      <div className="relative h-full" style={{ transform: 'translateZ(10px)', boxShadow: 'none' }}>
        {children}
      </div>
      {/* Static Shadow Overlay - Lifted to match content depth to prevent occlusion */}
      {staticShadow && (
        <div
          className="absolute inset-0 pointer-events-none rounded-[inherit]"
          style={{
            boxShadow: staticShadow,
            transform: 'translateZ(10px)'
          }}
        />
      )}
    </div>
  );
}

export { SpotlightCard };

export function SpotlightGroup({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`relative ${className}`}>
      {children}
    </div>
  );
}
